/**
 * speech.ts · 09_APP · OS-006 · capabilities C36, C39
 *
 * The browser's own speech recognition, no server and no key. Chrome (computer and Android) and
 * Safari (iPhone, iPad, Mac) have it; Firefox does not, and there the mic simply does not appear,
 * so typing always works (her words: "As well as type").
 *
 * Her catch, 2026-10-19: "it kind of cut me off." So it keeps listening until she taps the mic again,
 * shows her words as she speaks, and hands back the whole thing when she stops.
 */

import { useRef, useState } from "react";

type Result = { isFinal: boolean; 0: { transcript: string } };
type Rec = {
  lang: string; interimResults: boolean; continuous: boolean; maxAlternatives: number;
  onresult: ((e: { resultIndex: number; results: ArrayLike<Result> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void; stop(): void;
};

function ctor(): (new () => Rec) | null {
  const w = window as unknown as { SpeechRecognition?: new () => Rec; webkitSpeechRecognition?: new () => Rec };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function useDictation() {
  const [listening, setListening] = useState(false);
  const [live, setLive] = useState("");
  const [error, setError] = useState<string | null>(null);
  const rec = useRef<Rec | null>(null);
  const finalText = useRef("");
  const wanted = useRef(false);
  const done = useRef<((t: string) => void) | null>(null);
  const supported = typeof window !== "undefined" && ctor() !== null;

  function begin() {
    const C = ctor();
    if (!C) return;
    const r = new C();
    r.lang = "en-US";
    r.interimResults = true;
    r.continuous = true;
    r.maxAlternatives = 1;
    r.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i]!;
        if (res.isFinal) finalText.current = `${finalText.current} ${res[0].transcript}`.trim();
        else interim += res[0].transcript;
      }
      setLive(`${finalText.current} ${interim}`.trim());
    };
    r.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        wanted.current = false;
        setError("The browser blocked the microphone. Allow it for this site, or type instead.");
      }
    };
    r.onend = () => {
      // Some phones stop on a pause. If she has not tapped stop, keep listening.
      if (wanted.current) { try { r.start(); return; } catch { /* fall through and finish */ } }
      rec.current = null;
      setListening(false);
      setLive("");
      const t = finalText.current.trim();
      finalText.current = "";
      if (t && done.current) done.current(t);
    };
    rec.current = r;
    r.start();
  }

  /** Tap once to start, tap again to stop. onDone gets everything she said. */
  function toggle(onDone: (t: string) => void) {
    if (rec.current) { wanted.current = false; rec.current.stop(); return; }
    done.current = onDone;
    finalText.current = "";
    wanted.current = true;
    setError(null);
    setListening(true);
    begin();
  }

  return { supported, listening, live, error, toggle };
}
